//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { ScheduleInstagramPostErrorCode } from '@apis/publish/publishApiModels';
import {
  removeTagsAtIndex,
  reorderTags,
} from '@instagram-account/post-details/postDetailsUIUtils';
import { InstagramPostDetailsDefaultsActions } from '@instagram-account/redux/post-details/defaults/instagramPostDetailsDefaultsActions';
import InstagramPostDetailsDefaultsReducer from '@instagram-account/redux/post-details/defaults/instagramPostDetailsDefaultsReducer';
import { InstagramPostDetailsActions } from '@instagram-account/redux/post-details/instagramPostDetailsActions';
import {
  fallbackComparablePostDetails,
  ICombinedInstagramPostDetailsState,
  IInstagramPostDetailsState,
  INSTAGRAM_POST_DETAILS_ACTIONS,
  InstagramPostDetailsValidationError,
} from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import { InstagramPostDetailsMetadataActions } from '@instagram-account/redux/post-details/metadata/instagramPostDetailsMetadataActions';
import InstagramPostDetailsMetadataReducer from '@instagram-account/redux/post-details/metadata/instagramPostDetailsMetadataReducer';
import { InstagramPostDetailsModalActions } from '@instagram-account/redux/post-details/modal/instagramPostDetailsModalActions';
import InstagramPostDetailsModalReducer from '@instagram-account/redux/post-details/modal/instagramPostDetailsModalReducer';
import Cuid from '@models/Cuid';
import { InstagramPostType } from '@models/InstagramPostType';
import { move } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';

const initialState: IInstagramPostDetailsState = {
  /**
   * This will be overwritten in the setup actions, but including a fallback
   * here allows us to take out a lot of nullish checks/assertions
   */
  currentPostDetails: fallbackComparablePostDetails,
  errors: {
    fetchTopHashtagRecommendationsError: undefined,
    fetchSelectedMediaError: undefined,
    setInitialPostDetailsError: undefined,
  },
  flags: {
    hasSetPostDetails: false,
    isFetchingTopHashtagRecommendations: false,
    isFetchingSelectedMedia: false,
    isSchedulingPost: false,
    isDeletingPost: false,
  },
  hashtagRecommendations: [],
  initialPostDetails: undefined,
  schedulingEntryPoint: undefined,
  shouldCreateFbCrossPost: false,
  shouldCreateTwCrossPost: false,
  postType: InstagramPostType.Post,
  previewCuidIndex: 0,
  validationErrors: PojoSet.empty<InstagramPostDetailsValidationError>(),
  invalidCuids: PojoSet.empty<Cuid>(),
  isFirstCommentFieldExpanded: false,
};

function InstagramPostDetailsReducer(
  state: IInstagramPostDetailsState = initialState,
  action: InstagramPostDetailsActions,
): IInstagramPostDetailsState {
  switch (action.type) {
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_INITIAL_POST_DETAILS: {
      return {
        ...state,
        ...action.staticPostDetails,
        currentPostDetails: {
          ...action.comparablePostDetails,
        },
        errors: {
          ...state.errors,
          setInitialPostDetailsError: action.setInitialPostDetailsError,
        },
        flags: {
          ...state.flags,
          hasSetPostDetails: true,
        },
        initialPostDetails: {
          ...action.comparablePostDetails,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_SCHEDULING_ENTRY_POINT: {
      return {
        ...state,
        schedulingEntryPoint: action.schedulingEntryPoint,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_PREVIEW_CUID_INDEX: {
      return {
        ...state,
        previewCuidIndex: action.previewCuidIndex,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_SHOULD_CREATE_FB_CROSS_POST: {
      return {
        ...state,
        shouldCreateFbCrossPost: action.shouldCreateFbCrossPost,
        currentPostDetails: {
          ...state.currentPostDetails,
          shouldCreateFbCrossPost: action.shouldCreateFbCrossPost,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_SHOULD_CREATE_TW_CROSS_POST: {
      return {
        ...state,
        shouldCreateTwCrossPost: action.shouldCreateTwCrossPost,
        currentPostDetails: {
          ...state.currentPostDetails,
          shouldCreateTwCrossPost: action.shouldCreateTwCrossPost,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REQUEST_SELECTED_MEDIA: {
      // Reset the NoMedia validation error
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        InstagramPostDetailsValidationError.NoMedia,
      );
      const invalidCuids = PojoSet.empty<Cuid>();

      return {
        ...state,
        validationErrors,
        invalidCuids,
        errors: {
          ...state.errors,
          fetchSelectedMediaError: undefined,
        },
        flags: {
          ...state.flags,
          isFetchingSelectedMedia: true,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_SELECTED_MEDIA_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingSelectedMedia: false,
        },
        currentPostDetails: {
          ...state.currentPostDetails,
          selectedCuids: [
            ...state.currentPostDetails.selectedCuids,
            ...action.selectedCuids,
          ],
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_SELECTED_MEDIA_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchSelectedMediaError: action.message,
        },
        flags: {
          ...state.flags,
          isFetchingSelectedMedia: false,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.UPDATE_CUID_POSITION: {
      const selectedCuids = state.currentPostDetails.selectedCuids;
      const reorderedCuids: Cuid[] = move(
        selectedCuids,
        action.currIndex,
        action.newIndex,
      );

      // Update tagged user media indices
      const reorderedTaggedUsers = reorderTags(
        state.currentPostDetails.taggedUsers ?? [],
        action.currIndex,
        action.newIndex,
      );
      const reorderedTaggedProducts = reorderTags(
        state.currentPostDetails.taggedProducts ?? [],
        action.currIndex,
        action.newIndex,
      );

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          selectedCuids: reorderedCuids,
          taggedUsers: reorderedTaggedUsers,
          taggedProducts: reorderedTaggedProducts,
        },
        previewCuidIndex: action.newIndex,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_SELECTED_CUID: {
      const { removalIndex } = action;
      const selectedCuids = state.currentPostDetails.selectedCuids;
      const removalCuid = selectedCuids[removalIndex];

      const updatedSelectedCuids = [
        ...selectedCuids.slice(0, removalIndex),
        ...selectedCuids.slice(removalIndex + 1),
      ];

      // Remove tagged users and products at index and update media indices of
      // tagged users/products after index
      const updatedTaggedUsers = removeTagsAtIndex(
        state.currentPostDetails.taggedUsers ?? [],
        removalIndex,
      );
      const updatedTaggedProducts = removeTagsAtIndex(
        state.currentPostDetails.taggedProducts ?? [],
        removalIndex,
      );

      const invalidCuids = isDefined(removalCuid)
        ? PojoSet.remove(state.invalidCuids, removalCuid)
        : state.invalidCuids;

      // Reset Gif, InvalidFeedVideoSize, InvalidVideoLength, and InvalidMediaAspectRatio
      // validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.Gif,
          InstagramPostDetailsValidationError.InvalidFeedVideoSize,
          InstagramPostDetailsValidationError.InvalidIgImageSize,
          InstagramPostDetailsValidationError.InvalidMediaAspectRatio,
          InstagramPostDetailsValidationError.InvalidVideoLength,
        ]),
      );

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedUsers: updatedTaggedUsers,
          taggedProducts: updatedTaggedProducts,
          selectedCuids: updatedSelectedCuids,
        },
        invalidCuids,
        validationErrors,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_VIDEO_THUMBNAIL_OFFSET: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          videoThumbnailOffsetSeconds: action.videoThumbnailOffsetSeconds,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_VIDEO_THUMBNAIL_OFFSET: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          videoThumbnailOffsetSeconds: undefined,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_CAPTION: {
      // Reset the TooManyHashtags, and CaptionTooLong validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.TooManyHashtags,
          InstagramPostDetailsValidationError.CaptionTooLong,
        ]),
      );

      return {
        ...state,
        validationErrors,
        currentPostDetails: {
          ...state.currentPostDetails,
          caption: action.caption,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_FIRST_COMMENT: {
      // Reset the FirstCommentTooManyHashtags and FirstCommentTooLong validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.FirstCommentTooManyHashtags,
          InstagramPostDetailsValidationError.FirstCommentTooLong,
        ]),
      );

      return {
        ...state,
        validationErrors,
        currentPostDetails: {
          ...state.currentPostDetails,
          firstComment: action.firstComment,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.CLEAR_FIRST_COMMENT: {
      // Reset the FirstCommentTooManyHashtags and FirstCommentTooLong validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.FirstCommentTooManyHashtags,
          InstagramPostDetailsValidationError.FirstCommentTooLong,
        ]),
      );

      return {
        ...state,
        validationErrors,
        currentPostDetails: {
          ...state.currentPostDetails,
          firstComment: undefined,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.ADD_TAGGED_USER: {
      const currentTaggedUsers = state.currentPostDetails.taggedUsers ?? [];
      const newTaggedUsers = [...currentTaggedUsers, action.taggedUser];

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedUsers: newTaggedUsers,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_TAGGED_USER: {
      const currentTaggedUsers = state.currentPostDetails.taggedUsers ?? [];
      const newTaggedUsers = currentTaggedUsers.filter(
        (userTag) =>
          !(
            userTag.username === action.username &&
            userTag.mediaIndex === action.mediaIndex
          ),
      );

      const scheduleErrors =
        state.errors.scheduleFailureCode ===
        ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_INVALID_TAGGED_USER
          ? { scheduleFailureCode: undefined, scheduleFailureError: undefined }
          : {
              scheduleFailureCode: state.errors.scheduleFailureCode,
              scheduleFailureError: state.errors.scheduleFailureError,
            };

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedUsers: newTaggedUsers,
        },
        errors: {
          ...state.errors,
          ...scheduleErrors,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.UPDATE_TAGGED_USER: {
      const currentTaggedUsers = state.currentPostDetails.taggedUsers ?? [];
      const newTaggedUsers = currentTaggedUsers.map((u) =>
        u.username === action.taggedUser.username &&
        u.mediaIndex === action.taggedUser.mediaIndex
          ? action.taggedUser
          : u,
      );

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedUsers: newTaggedUsers,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_ALL_TAGGED_USERS: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedUsers: undefined,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.ADD_TAGGED_PRODUCT: {
      const currentTaggedProducts =
        state.currentPostDetails.taggedProducts ?? [];
      const newTaggedProducts = [
        ...currentTaggedProducts,
        action.taggedProduct,
      ];

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedProducts: newTaggedProducts,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_TAGGED_PRODUCT: {
      const currentTaggedProducts =
        state.currentPostDetails.taggedProducts ?? [];
      const newTaggedProducts = currentTaggedProducts.filter(
        (productTag) =>
          !(
            productTag.productId === action.productId &&
            productTag.mediaIndex === action.mediaIndex
          ),
      );

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedProducts: newTaggedProducts,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.UPDATE_TAGGED_PRODUCT: {
      const currentTaggedProducts =
        state.currentPostDetails.taggedProducts ?? [];
      const newTaggedProducts = currentTaggedProducts.map((productTag) =>
        productTag.productId === action.taggedProduct.productId &&
        productTag.mediaIndex === action.taggedProduct.mediaIndex
          ? action.taggedProduct
          : productTag,
      );

      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedProducts: newTaggedProducts,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_ALL_TAGGED_PRODUCTS: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedProducts: undefined,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_TAGGED_LOCATION: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedLocation: action.taggedLocation,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REMOVE_TAGGED_LOCATION: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          taggedLocation: undefined,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_POST_TIME: {
      // Reset the NoPostTime and PostTimeInPast validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.NoPostTime,
          InstagramPostDetailsValidationError.PostTimeInPast,
        ]),
      );

      return {
        ...state,
        validationErrors,
        currentPostDetails: {
          ...state.currentPostDetails,
          postTime: action.postTime,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_NOTIFICATION_USER: {
      // Reset the NoNotificationUser validation error
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        InstagramPostDetailsValidationError.NoNotificationUser,
      );

      return {
        ...state,
        validationErrors,
        currentPostDetails: {
          ...state.currentPostDetails,
          notificationUserId: action.notificationUserId,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_PUBLISHING_STRATEGY: {
      return {
        ...state,
        currentPostDetails: {
          ...state.currentPostDetails,
          publishingStrategy: action.publishingStrategy,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: action.validationErrors,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_INVALID_CUIDS: {
      return {
        ...state,
        invalidCuids: action.invalidCuids,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REQUEST_SCHEDULE_POST: {
      return {
        ...state,
        errors: {
          ...state.errors,
          scheduleFailureCode: undefined,
        },
        flags: {
          ...state.flags,
          isSchedulingPost: true,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_SCHEDULE_POST_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isSchedulingPost: false,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_SCHEDULE_POST_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          scheduleFailureCode: action.errorCode,
          scheduleFailureError: action.message,
        },
        flags: {
          ...state.flags,
          isSchedulingPost: false,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RESET_POST_DETAILS: {
      return initialState;
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REQUEST_DELETE_POST: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isDeletingPost: true,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_DELETE_POST_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isDeletingPost: false,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_DELETE_POST_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isDeletingPost: false,
        },
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.SET_IS_FIRST_COMMENT_FIELD_EXPANDED: {
      return {
        ...state,
        isFirstCommentFieldExpanded: action.isFirstCommentFieldExpanded,
      };
    }
    case INSTAGRAM_POST_DETAILS_ACTIONS.REQUEST_TOP_HASHTAG_RECOMMENDATIONS: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchTopHashtagRecommendationsError: undefined,
        },
        flags: {
          ...state.flags,
          isFetchingTopHashtagRecommendations: true,
        },
      };
    }

    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_SUCCEEDED: {
      return {
        ...state,
        hashtagRecommendations: action.recommendedHashtags,
        flags: {
          ...state.flags,
          isFetchingTopHashtagRecommendations: false,
        },
      };
    }

    case INSTAGRAM_POST_DETAILS_ACTIONS.RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchTopHashtagRecommendationsError: action.message,
        },
        flags: {
          ...state.flags,
          isFetchingTopHashtagRecommendations: false,
        },
      };
    }
  }
  return state;
}

export default function CombinedInstagramPostDetailsReducer(
  state: ICombinedInstagramPostDetailsState | undefined,
  action:
    | InstagramPostDetailsActions
    | InstagramPostDetailsDefaultsActions
    | InstagramPostDetailsMetadataActions
    | InstagramPostDetailsModalActions,
): ICombinedInstagramPostDetailsState {
  return {
    ...InstagramPostDetailsReducer(
      state,
      action as InstagramPostDetailsActions,
    ),
    defaults: InstagramPostDetailsDefaultsReducer(
      state?.defaults,
      action as InstagramPostDetailsDefaultsActions,
    ),
    metadata: InstagramPostDetailsMetadataReducer(
      state?.metadata,
      action as InstagramPostDetailsMetadataActions,
    ),
    modals: InstagramPostDetailsModalReducer(
      state?.modals,
      action as InstagramPostDetailsModalActions,
    ),
  };
}
